import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { AffiliatePartner } from '@/hooks/useAffiliatePartners';

export default function AffiliatePartners() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<AffiliatePartner | null>(null);
  const [formData, setFormData] = useState({
    partner_name: '',
    category: '',
    affiliate_url: '',
    description: '',
    logo_url: '',
    active: true,
    display_order: 0,
    personal_endorsement: '',
    star_rating: 0,
    pros: [] as string[],
    cons: [] as string[],
    button_text: 'Book Now',
    featured: false,
  });
  const [prosInput, setProsInput] = useState('');
  const [consInput, setConsInput] = useState('');

  const queryClient = useQueryClient();

  const { data: partners, isLoading } = useQuery({
    queryKey: ['admin-affiliate-partners'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('affiliate_partners')
        .select('*')
        .order('display_order', { ascending: true });
      if (error) throw error;
      return data as AffiliatePartner[];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase.from('affiliate_partners').insert(data);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-affiliate-partners'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-partners'] });
      toast.success('Partner created successfully');
      resetForm();
    },
    onError: (error) => {
      toast.error('Failed to create partner: ' + error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const { error } = await supabase
        .from('affiliate_partners')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-affiliate-partners'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-partners'] });
      toast.success('Partner updated successfully');
      resetForm();
    },
    onError: (error) => {
      toast.error('Failed to update partner: ' + error.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('affiliate_partners')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-affiliate-partners'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-partners'] });
      toast.success('Partner deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete partner: ' + error.message);
    },
  });

  const resetForm = () => {
    setFormData({
      partner_name: '',
      category: '',
      affiliate_url: '',
      description: '',
      logo_url: '',
      active: true,
      display_order: 0,
      personal_endorsement: '',
      star_rating: 0,
      pros: [],
      cons: [],
      button_text: 'Book Now',
      featured: false,
    });
    setProsInput('');
    setConsInput('');
    setEditingPartner(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPartner) {
      updateMutation.mutate({ id: editingPartner.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (partner: AffiliatePartner) => {
    setEditingPartner(partner);
    setFormData({
      partner_name: partner.partner_name,
      category: partner.category,
      affiliate_url: partner.affiliate_url,
      description: partner.description,
      logo_url: partner.logo_url || '',
      active: partner.active,
      display_order: partner.display_order,
      personal_endorsement: partner.personal_endorsement || '',
      star_rating: partner.star_rating || 0,
      pros: partner.pros || [],
      cons: partner.cons || [],
      button_text: partner.button_text || 'Book Now',
      featured: partner.featured,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this partner?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleReorder = (partner: AffiliatePartner, direction: 'up' | 'down') => {
    const currentIndex = partners?.findIndex((p) => p.id === partner.id) ?? -1;
    if (currentIndex === -1) return;

    const newOrder = direction === 'up' ? partner.display_order - 1 : partner.display_order + 1;
    updateMutation.mutate({ id: partner.id, data: { display_order: newOrder } });
  };

  return (
    <div className="container mx-auto py-8 max-w-7xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Affiliate Partners</h1>
          <p className="text-muted-foreground mt-2">
            Manage travel partners displayed on the Travel Resources page
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Partner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</DialogTitle>
              <DialogDescription>
                {editingPartner ? 'Update partner information' : 'Add a new travel partner'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Basic Information</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="partner_name">Partner Name *</Label>
                      <Input
                        id="partner_name"
                        value={formData.partner_name}
                        onChange={(e) => setFormData({ ...formData, partner_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Input
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="e.g., Flights, Hotels, Tours"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="affiliate_url">Affiliate URL *</Label>
                    <Input
                      id="affiliate_url"
                      type="url"
                      value={formData.affiliate_url}
                      onChange={(e) => setFormData({ ...formData, affiliate_url: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      required
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="logo_url">Logo URL (optional)</Label>
                      <Input
                        id="logo_url"
                        type="url"
                        value={formData.logo_url}
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="button_text">Button Text</Label>
                      <Input
                        id="button_text"
                        value={formData.button_text}
                        onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
                        placeholder="Book Now"
                      />
                    </div>
                  </div>
                </div>

                {/* Personal Touch */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-lg font-semibold">Personal Touch</h3>
                  <div className="space-y-2">
                    <Label htmlFor="personal_endorsement">Personal Endorsement</Label>
                    <Textarea
                      id="personal_endorsement"
                      value={formData.personal_endorsement}
                      onChange={(e) => setFormData({ ...formData, personal_endorsement: e.target.value })}
                      placeholder="Share your personal experience with this partner..."
                      rows={2}
                    />
                    <p className="text-sm text-muted-foreground">
                      Example: "I've booked 50+ tours through Klook - their instant confirmation is a lifesaver!"
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="star_rating">Rating (out of 5)</Label>
                    <Input
                      id="star_rating"
                      type="number"
                      min="0"
                      max="5"
                      step="0.5"
                      value={formData.star_rating}
                      onChange={(e) => setFormData({ ...formData, star_rating: parseFloat(e.target.value) })}
                    />
                  </div>
                </div>

                {/* Pros & Cons */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-lg font-semibold">Pros & Cons</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Pros</Label>
                      <div className="flex gap-2">
                        <Input
                          value={prosInput}
                          onChange={(e) => setProsInput(e.target.value)}
                          placeholder="Enter a pro..."
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (prosInput.trim()) {
                                setFormData({ ...formData, pros: [...formData.pros, prosInput.trim()] });
                                setProsInput('');
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (prosInput.trim()) {
                              setFormData({ ...formData, pros: [...formData.pros, prosInput.trim()] });
                              setProsInput('');
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {formData.pros.map((pro, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                            <span className="text-sm">• {pro}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData({ 
                                  ...formData, 
                                  pros: formData.pros.filter((_, i) => i !== idx) 
                                });
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Cons</Label>
                      <div className="flex gap-2">
                        <Input
                          value={consInput}
                          onChange={(e) => setConsInput(e.target.value)}
                          placeholder="Enter a con..."
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (consInput.trim()) {
                                setFormData({ ...formData, cons: [...formData.cons, consInput.trim()] });
                                setConsInput('');
                              }
                            }
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            if (consInput.trim()) {
                              setFormData({ ...formData, cons: [...formData.cons, consInput.trim()] });
                              setConsInput('');
                            }
                          }}
                        >
                          Add
                        </Button>
                      </div>
                      <div className="space-y-1">
                        {formData.cons.map((con, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                            <span className="text-sm">• {con}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setFormData({ 
                                  ...formData, 
                                  cons: formData.cons.filter((_, i) => i !== idx) 
                                });
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-lg font-semibold">Settings</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="display_order">Display Order</Label>
                      <Input
                        id="display_order"
                        type="number"
                        value={formData.display_order}
                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="active"
                          checked={formData.active}
                          onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                        />
                        <Label htmlFor="active">Active</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="featured"
                          checked={formData.featured}
                          onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                        />
                        <Label htmlFor="featured">Featured (Recommended)</Label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingPartner ? 'Update Partner' : 'Create Partner'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Partners</CardTitle>
          <CardDescription>
            {partners?.length || 0} partners configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p>Loading...</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners?.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReorder(partner, 'up')}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">{partner.display_order}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReorder(partner, 'down')}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {partner.partner_name}
                        {partner.featured && (
                          <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                            Featured
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{partner.category}</TableCell>
                    <TableCell>
                      {partner.star_rating ? (
                        <span className="text-sm">⭐ {partner.star_rating.toFixed(1)}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          partner.active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {partner.active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(partner)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(partner.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
